import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { BrowserProvider, JsonRpcSigner, Network } from 'ethers';

// Paseo AssetHub network config
const PASEO_ASSETHUB = {
  chainId: '0x190FC4E6', // 420420422
  chainName: 'Paseo AssetHub Testnet',
  nativeCurrency: {
    name: 'PAS',
    symbol: 'PAS',
    decimals: 18,
  },
  rpcUrls: ['https://testnet-passet-hub-eth-rpc.polkadot.io'],
  blockExplorerUrls: ['https://blockscout-passet-hub.parity-testnet.parity.io'],
};

interface WalletContextType {
  provider: BrowserProvider | null;
  signer: JsonRpcSigner | null;
  address: string | null;
  chainId: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  switchNetwork: () => Promise<void>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWalletContext = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWalletContext must be used within WalletProvider');
  }
  return context;
};

interface WalletProviderProps {
  children: React.ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [provider, setProvider] = useState<BrowserProvider | null>(null);
  const [signer, setSigner] = useState<JsonRpcSigner | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [chainId, setChainId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConnected = Boolean(address && provider);

  // Check if MetaMask/wallet is installed
  const checkWalletInstalled = useCallback(() => {
    if (typeof window.ethereum === 'undefined') {
      throw new Error('Please install MetaMask or a compatible Web3 wallet');
    }
  }, []);

  // Switch to Paseo AssetHub network
  const switchNetwork = useCallback(async () => {
    checkWalletInstalled();

    try {
      // Try to switch to the network
      await window.ethereum!.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: PASEO_ASSETHUB.chainId }],
      });
    } catch (switchError: unknown) {
      const errorCode = (switchError as { code?: number }).code;

      // Network doesn't exist (4902) or unrecognized chain (might vary by wallet)
      if (errorCode === 4902 || errorCode === -32603) {
        try {
          await window.ethereum!.request({
            method: 'wallet_addEthereumChain',
            params: [PASEO_ASSETHUB],
          });
        } catch (addError) {
          // User rejected or wallet doesn't support adding networks
          console.warn('Could not add Paseo network:', addError);
          // Don't throw - just log and continue (user might already be on correct network)
        }
      } else if (errorCode === 4001) {
        // User rejected the switch - that's fine
        console.log('User rejected network switch');
      } else {
        // Unknown error - log but don't throw to prevent infinite loops
        console.warn('Network switch error:', switchError);
      }
    }
  }, [checkWalletInstalled]);

  // Connect wallet
  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      checkWalletInstalled();

      // Request account access
      const accounts = await window.ethereum!.request({
        method: 'eth_requestAccounts',
      }) as string[];

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found');
      }

      // Create provider and signer
      const web3Provider = new BrowserProvider(window.ethereum!);
      const web3Signer = await web3Provider.getSigner();
      const network: Network = await web3Provider.getNetwork();

      setProvider(web3Provider);
      setSigner(web3Signer);
      setAddress(accounts[0] ?? null);
      setChainId(`0x${network.chainId.toString(16)}`);

      // Check if we're on the right network
      if (`0x${network.chainId.toString(16)}` !== PASEO_ASSETHUB.chainId) {
        // Auto-switch to Paseo AssetHub
        await switchNetwork();
        // Refresh after switch
        const newNetwork: Network = await web3Provider.getNetwork();
        setChainId(`0x${newNetwork.chainId.toString(16)}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect wallet';
      setError(errorMessage);
      console.error('Wallet connection error:', err);
    } finally {
      setIsConnecting(false);
    }
  }, [checkWalletInstalled, switchNetwork]);

  // Disconnect wallet
  const disconnect = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAddress(null);
    setChainId(null);
    setError(null);
  }, []);

  // Listen for account changes
  useEffect(() => {
    if (typeof window.ethereum === 'undefined') return;

    const handleAccountsChanged = (accounts: unknown) => {
      const accountArray = accounts as string[];
      if (accountArray.length === 0) {
        disconnect();
      } else {
        setAddress(accountArray[0] ?? null);
      }
    };

    const handleChainChanged = (newChainId: unknown) => {
      setChainId(newChainId as string);
      // Reload to ensure clean state
      window.location.reload();
    };

    const handleDisconnect = () => {
      disconnect();
    };

    window.ethereum!.on('accountsChanged', handleAccountsChanged);
    window.ethereum!.on('chainChanged', handleChainChanged);
    window.ethereum!.on('disconnect', handleDisconnect);

    return () => {
      window.ethereum!.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum!.removeListener('chainChanged', handleChainChanged);
      window.ethereum!.removeListener('disconnect', handleDisconnect);
    };
  }, [disconnect]);

  // Auto-connect if previously connected (runs once on mount)
  useEffect(() => {
    let mounted = true;

    const autoConnect = async () => {
      if (typeof window.ethereum === 'undefined') return;

      try {
        const accounts = await window.ethereum.request({
          method: 'eth_accounts',
        });

        if (mounted && accounts && (accounts as string[]).length > 0) {
          // Inline connection logic to avoid dependency on connect
          const web3Provider = new BrowserProvider(window.ethereum!);
          const web3Signer = await web3Provider.getSigner();
          const network: Network = await web3Provider.getNetwork();

          if (mounted) {
            setProvider(web3Provider);
            setSigner(web3Signer);
            setAddress((accounts as string[])[0] ?? null);
            setChainId(`0x${network.chainId.toString(16)}`);
          }
        }
      } catch (err) {
        console.error('Auto-connect failed:', err);
      }
    };

    autoConnect();

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  const value: WalletContextType = {
    provider,
    signer,
    address,
    chainId,
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect,
    switchNetwork,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      on: (event: string, callback: (params: unknown) => void) => void;
      removeListener: (event: string, callback: (params: unknown) => void) => void;
      isMetaMask?: boolean;
      isTalisman?: boolean;
    };
  }
}
